import React, { useState, useEffect } from "react";
import SidebarNav from "../../components/MasterSideBar"; // Standardized Import
import TopbarNav from "../../components/TopbarNav";
import LeaveDecisionModal from "../../components/LeaveDecisionModal";
import LeaveApprovalTable from "../../components/LeaveApprovalTable";
import LeaveTracker from "../../components/LeaveTracker";
import Card from "../../components/card";
import LeaveRequestForm from "../../components/LeaveRequestForm";
import { FiPlus, FiCheckCircle, FiXCircle, FiClock } from "react-icons/fi";

const API_URL = "https://ims.holdthechild.net";

export default function PDLeaveRequestsPage() {
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [feedbackMsg, setFeedbackMsg] = useState("");
  const [showLeaveForm, setShowLeaveForm] = useState(false);

  // Sidebar state
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const trackerMonths = ["July", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const departments = ["HRs", "PTAs", "FS", "General"];

  const token = localStorage.getItem("token");
  const user = JSON.parse(localStorage.getItem("user"));
  const userRole = user ? user.role : null;
  const staffId = user ? user.id : null;

  useEffect(() => {
    const fetchLeaveRequests = async () => {
      try {
        const res = await fetch(`${API_URL}/api/leave-request`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        if (!res.ok || !data.success) throw new Error("Failed to fetch");
        setLeaveRequests(data.data);
      } catch (err) {
        console.error("Error loading leave requests:", err);
        setFeedbackMsg("⚠️ Failed to load leave requests.");
      }
    };
    fetchLeaveRequests();
  }, [token]);

  // Calculations
  const approvedCount = leaveRequests.filter((r) => r.status === "Approved").length;
  const rejectedCount = leaveRequests.filter((r) => r.status === "Rejected").length;
  const pendingCount = leaveRequests.filter((r) => r.status === "Pending" || r.status === "On Hold").length;
  const totalRequests = leaveRequests.length;

  const approvedPercent = totalRequests > 0 ? ((approvedCount / totalRequests) * 100).toFixed(1) : 0;
  const rejectedPercent = totalRequests > 0 ? ((rejectedCount / totalRequests) * 100).toFixed(1) : 0;
  const pendingPercent = totalRequests > 0 ? ((pendingCount / totalRequests) * 100).toFixed(1) : 0;

  const handleSubmitDecision = async ({ status, comment }) => {
    try {
      const res = await fetch(`${API_URL}/api/leave-request/${selectedRequest._id}/status`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ status, comment }),
      });
      const updated = await res.json();
      if (!res.ok) throw new Error(updated.message || "Update failed");

      setFeedbackMsg("✅ Leave updated successfully");
      setShowModal(false);
      setLeaveRequests((prev) =>
        prev.map((req) => (req._id === selectedRequest._id ? updated.leaveRequest : req))
      );
    } catch (err) {
      console.error(err);
      setFeedbackMsg("❌ Failed to update leave.");
    }
  };

  return (
    <div className="flex min-h-screen bg-gray-50 font-sans">
      {/* 1. Master Sidebar */}
      <SidebarNav visible={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      {/* 2. Content Wrapper - Handles margin and flex */}
      <div className="flex-1 flex flex-col md:ml-64 transition-all duration-300">
        
        {/* 3. TopBar */}
        <TopbarNav onSidebarToggle={() => setSidebarOpen(true)} />

        <main className="p-4 md:p-8 mt-16 lg:mt-20 space-y-8">
          {/* Feedback Toast */}
          {feedbackMsg && (
            <div className="fixed top-20 left-1/2 transform -translate-x-1/2 z-[100] bg-gray-800 text-white px-6 py-2 rounded-full shadow-xl text-sm font-bold">
              {feedbackMsg}
            </div>
          )}

          {/* Header Section */}
          <section className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-black text-gray-800 uppercase tracking-tight">
                Leave Management
              </h1>
              <p className="text-gray-500 text-sm">Review staff requests and track departmental leave volume.</p>
            </div>
            <button
              onClick={() => setShowLeaveForm(true)}
              className="flex items-center justify-center gap-2 bg-orange-600 text-white px-6 py-3 rounded-xl font-bold hover:bg-orange-700 shadow-lg shadow-orange-200 transition-all active:scale-95 text-sm"
            >
              <FiPlus size={18} />
              Apply for Leave
            </button>
          </section>

          {/* KPI and Tracker Row */}
          <section className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left: Leave Tracker Chart/Component */}
            <div className="lg:col-span-2 bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
              <h2 className="text-sm font-bold text-gray-400 uppercase mb-4 tracking-wider">Leave Distribution</h2>
              <LeaveTracker departments={departments} trackerMonths={trackerMonths} />
            </div>

            {/* Right: Status Summaries */}
            <div className="flex flex-col gap-4">
              <Card 
                title="Approved" 
                value={approvedCount} 
                icon={<FiCheckCircle className="text-green-500" />} 
                progress={Number(approvedPercent)} 
                size="small" 
              />
              <Card 
                title="Rejected" 
                value={rejectedCount} 
                icon={<FiXCircle className="text-red-500" />} 
                progress={Number(rejectedPercent)} 
                size="small" 
              />
              <Card 
                title="Pending/Hold" 
                value={pendingCount} 
                icon={<FiClock className="text-yellow-500" />} 
                progress={Number(pendingPercent)} 
                size="small" 
              />
            </div>
          </section>

          {/* Leave approval table */}
          <section className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="p-6 border-b border-gray-50">
              <h2 className="text-lg font-bold text-gray-800 uppercase">Requests Awaiting Decision</h2>
            </div>
            <div className="p-4 overflow-x-auto">
              <LeaveApprovalTable
                leaveRequests={leaveRequests}
                onTakeAction={(req) => {
                  setSelectedRequest(req);
                  setShowModal(true);
                }}
                feedbackMsg={feedbackMsg}
                showModal={showModal}
                LeaveDecisionModal={LeaveDecisionModal}
                selectedRequest={selectedRequest}
                onCloseModal={() => setShowModal(false)}
                onSubmitDecision={handleSubmitDecision}
                userRole={userRole}
                token={token}
              />
            </div>
          </section>
        </main>
      </div>

      {/* Leave Request Form Modal */}
      {showLeaveForm && staffId && token && (
        <div 
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[100] px-4" 
          onClick={() => setShowLeaveForm(false)}
        >
          <div 
            className="bg-white rounded-2xl shadow-2xl p-8 max-w-2xl w-full relative animate-in fade-in zoom-in duration-200" 
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowLeaveForm(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <FiXCircle size={24} />
            </button>
            <div className="mb-6">
               <h2 className="text-xl font-bold text-gray-800">Submit Leave Request</h2>
               <p className="text-sm text-gray-500">Please fill in the details for your leave application.</p>
            </div>
            <LeaveRequestForm staffId={staffId} token={token} on处Success={() => setShowLeaveForm(false)} />
          </div>
        </div>
      )}
    </div>
  );
}